import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  input,
} from '@angular/core';
import {
  SpritesPaths,
  SPRITES_PATHS_TOKEN,
} from '@epicuro-next/ataraxia/models';
import { dynamicElementsProductCardIcons } from '@epicuro-next/constants/dynamic-elements';
import {
  newIconText,
  noosIconText,
  productIconsTooltipLabels,
} from '@epicuro-next/constants/features/localization';
import { genderIcons } from '@epicuro-next/constants/ui/icons';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  formatDateToLocaleDate,
  Placement,
  toBoolean,
} from '@epicuro-next/utilities';
import { Product } from 'graphql-api/gql-api-interfaces';

@DynamicComponentRegister(dynamicElementsProductCardIcons, true)
@Component({
  selector: 'atx-product-card-icons',
  styleUrls: ['./product-card-icons.component.scss'],
  templateUrl: './product-card-icons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardIconsComponent {
  public readonly Placement = Placement;
  private marketingSupportFormatDate = 'dd/MM/yyyy';
  public marketingSupportIconText: string;

  @Input() public product: Product;
  @Input() public showPopularityIcon = true;
  @Input() public showMraIcon = true;
  @Input() public showGenderIcon = false;
  @Input() public set language(value: string) {
    const marketingSupportEolDate =
      this.product?.icons?.marketingSupportEolDate;

    this.marketingSupportIconText = $localize`:Product card|marketing support icon tooltip@@marketingIconSupportTooltip:Has marketing support until ${formatDateToLocaleDate(
      new Date(marketingSupportEolDate),
      value,
      this.marketingSupportFormatDate,
    )}`;
  }
  @Input() public dateFormat? = 'MMM y';
  @Input() public showEstimatedDeliveryDate = true;
  public showNewIcon = input(true);

  public readonly noosText = noosIconText;
  public readonly newText = newIconText;
  public readonly genderIcons = genderIcons;

  public readonly popularityIconTooltip = productIconsTooltipLabels.popularity;
  public productCardTooltip = $localize`:ProductCard|Product Card Tooltip@@productCardTooltip:Earliest Delivery Date for Pre-Order`;

  constructor(@Inject(SPRITES_PATHS_TOKEN) public spritesPaths: SpritesPaths) {}

  public get genderIcon(): string[] | null | undefined {
    return this.genderIconEntry?.icon;
  }

  public get genderIconTooltipText(): string | undefined {
    return this.genderIconEntry?.label;
  }

  public get genderIconClassName(): string | undefined {
    return this.genderIconEntry?.className;
  }

  public get genderIconText(): string | undefined {
    return this.genderIconTooltipText?.toLocaleUpperCase();
  }

  public get isMraShown() {
    return (
      this.showMraIcon &&
      toBoolean((this.product.attributes.mra ?? '') as string)
    );
  }

  public get genderIconEntry():
    | typeof this.genderIcons[keyof typeof this.genderIcons]
    | undefined {
    const gender = this.product.attributes.gender as string;
    if (gender in this.genderIcons) {
      return this.genderIcons[gender as keyof typeof this.genderIcons];
    }

    return undefined;
  }
}
